import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import styled, { css } from "styled-components/macro";
import { themeVariables } from "../../themeVariables";

type AvatarKind = "feedback" | "sudowrite" | "user";

const sudowriteAvatarSrc = "/assets/sudowrite-avatar.svg";
const blankAvatarSrc = "/assets/blank-avatar.svg";
const sudowriteGradient = "linear-gradient(180deg, #FFEED3 0%, #FFC0EF 50%)";
const defaultPrimaryCTATransparent = "var(--primary-cta-transparent, rgba(72, 8, 209, 0.07))";
const defaultPrimaryCTATransparent30 = "var(--primary-cta-transparent-30, rgba(72, 8, 209, 0.3))";
const defaultPrimaryCTAActive = "var(--primary-cta-active, #7240dd)";
const defaultSelectedElementBackground = "var(--selected-element-background, rgba(255, 255, 255, 0.12))";
const sepiaPrimaryCTA = "var(--primary-cta, #c06800)";
const sepiaPrimaryCTATransparent = "var(--primary-cta-transparent, rgba(192, 104, 0, 0.07))";

interface CommentAvatarProps {
  kind: AvatarKind;
  label: string;
  photoURL?: string | null;
  iconSrc?: string | null;
}

export function CommentAvatar({ kind, label, photoURL, iconSrc }: CommentAvatarProps): JSX.Element {
  const ariaLabel = `${label} profile image`;
  const safePhotoURL = typeof photoURL === "string" && photoURL.trim() ? photoURL.trim() : null;
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    setPhotoFailed(false);
  }, [safePhotoURL]);

  if (kind === "feedback" && iconSrc) {
    return (
      <FeedbackIconAvatar
        role="img"
        aria-label={ariaLabel}
        data-testid="comment-avatar-feedback"
      >
        <SVG
          src={iconSrc}
          aria-hidden={true}
          width={24}
          height={24}
        />
      </FeedbackIconAvatar>
    );
  }

  if (kind === "user" && safePhotoURL && !photoFailed) {
    return (
      <AvatarFrame
        role="img"
        aria-label={ariaLabel}
        $kind="photo"
      >
        <AvatarPhoto
          src={safePhotoURL}
          alt=""
          data-testid="comment-avatar-photo"
          onError={() => setPhotoFailed(true)}
        />
      </AvatarFrame>
    );
  }

  if (kind === "sudowrite") {
    return (
      <AvatarFrame
        role="img"
        aria-label={ariaLabel}
        $kind="sudowrite"
        data-testid="comment-avatar-sudowrite"
      >
        <SudowriteInlineIcon
          src={sudowriteAvatarSrc}
          aria-hidden={true}
        />
      </AvatarFrame>
    );
  }

  return (
    <AvatarFrame
      role="img"
      aria-label={ariaLabel}
      $kind={kind}
      data-testid="comment-avatar-user-fallback"
    >
      <BlankAvatarIcon
        src={blankAvatarSrc}
        aria-hidden={true}
      />
    </AvatarFrame>
  );
}

const avatarBase = css`
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
`;

const FeedbackIconAvatar = styled.span`
  ${avatarBase}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${themeVariables.grey1};

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor;
  }
`;

const AvatarFrame = styled.span<{ $kind: AvatarKind | "photo" }>`
  ${avatarBase}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 999px;
  color: ${defaultPrimaryCTATransparent30};
  background: ${defaultPrimaryCTATransparent};

  ${({ $kind }) =>
    $kind === "sudowrite" &&
    css`
      color: var(--grey-0, #383838);
      background: ${sudowriteGradient};

      body[data-theme="sepia"] & {
        color: ${sepiaPrimaryCTA};
        background: ${sepiaPrimaryCTATransparent};
      }

      body[data-theme-type="dark"] & {
        color: var(--grey-0, rgba(255, 255, 255, 0.95));
        background: ${defaultSelectedElementBackground};
      }
    `}

  ${({ $kind }) =>
    $kind !== "sudowrite" &&
    css`
      body[data-theme-type="dark"] & {
        color: ${defaultPrimaryCTAActive};
        background: ${defaultSelectedElementBackground};
      }
    `}
`;

const SudowriteInlineIcon = styled(SVG)`
  display: block;
  width: 100%;
  height: 100%;

  circle {
    fill: transparent;
  }

  path {
    fill: currentColor;
  }
`;

const BlankAvatarIcon = styled(SVG)`
  display: block;
  width: 100%;
  height: 100%;

  circle:first-of-type {
    fill: ${defaultPrimaryCTATransparent};
    fill-opacity: 1;
  }

  circle:not(:first-of-type),
  path {
    fill: currentColor;
    fill-opacity: 1;
  }

  body[data-theme-type="dark"] & {
    circle:first-of-type {
      fill: ${defaultSelectedElementBackground};
    }
  }
`;

const AvatarPhoto = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  outline: 1px solid rgba(0, 0, 0, 0.1);
  outline-offset: -1px;

  body[data-theme-type="dark"] & {
    outline-color: rgba(255, 255, 255, 0.1);
  }
`;

export default CommentAvatar;
